<template>
  <div class="drama-config-container">
    <div class="drama-config-content">
      <div class="drama-config-header">
        <h2>短剧创作设置</h2>
        <p>配置短剧创作模块的参数和模板</p>
      </div>

      <div class="config-section">
        <h3>基础配置</h3>
        <div class="config-grid">
          <div class="config-item">
            <label>默认时长</label>
            <el-select v-model="config.defaults.duration" class="select">
              <el-option label="1分钟" value="1"></el-option>
              <el-option label="3分钟" value="3"></el-option>
              <el-option label="5分钟" value="5"></el-option>
            </el-select>
          </div>
          <div class="config-item">
            <label>默认风格</label>
            <el-select v-model="config.defaults.style" class="select">
              <el-option label="现代" value="modern"></el-option>
              <el-option label="古风" value="ancient"></el-option>
              <el-option label="奇幻" value="fantasy"></el-option>
              <el-option label="科幻" value="sci-fi"></el-option>
            </el-select>
          </div>
          <div class="config-item length-item">
            <label>最大剧本长度</label>
            <div class="length-input-container">
              <el-input-number v-model="config.maxScriptLength" :min="100" :max="5000" class="number-input"></el-input-number>
              <span class="unit">字</span>
            </div>
          </div>
          <div class="config-item">
            <label>自动生成分镜</label>
            <el-switch v-model="config.autoStoryboard"></el-switch>
          </div>
        </div>
      </div>

      <div class="config-section">
        <h3>风格配置</h3>
        <div class="style-tags">
          <el-tag
            v-for="(style, key) in config.styles"
            :key="key"
            :type="style.enabled ? 'success' : 'info'"
            closable
            @close="removeStyle(key)"
          >
            {{ style.name }} - {{ style.description }}
          </el-tag>
          <el-button size="small" @click="addStyleDialogVisible = true">
            添加风格
          </el-button>
        </div>
      </div>

      <div class="config-section">
        <h3>提示词模板</h3>
        <div class="template-section">
          <div class="template-item">
            <label>分镜生成提示词</label>
            <el-input
              type="textarea"
              v-model="config.templates.storyboard"
              :rows="4"
              placeholder="分镜生成的提示词模板"
              class="template-input"
            ></el-input>
          </div>
          <div class="template-item">
            <label>角色生成提示词</label>
            <el-input
              type="textarea"
              v-model="config.templates.character"
              :rows="4"
              placeholder="角色生成的提示词模板"
              class="template-input"
            ></el-input>
          </div>
          <div class="template-item">
            <label>场景生成提示词</label>
            <el-input
              type="textarea"
              v-model="config.templates.scene"
              :rows="4"
              placeholder="场景生成的提示词模板"
              class="template-input"
            ></el-input>
          </div>
        </div>
      </div>

      <div class="config-section">
        <h3>生成选项</h3>
        <div class="options-grid">
          <div class="option-item">
            <el-checkbox v-model="config.options.generateAudio">生成音频</el-checkbox>
          </div>
          <div class="option-item">
            <el-checkbox v-model="config.options.generateSubtitles">生成字幕</el-checkbox>
          </div>
          <div class="option-item">
            <el-checkbox v-model="config.options.optimizeQuality">优化视频质量</el-checkbox>
          </div>
          <div class="option-item">
            <el-checkbox v-model="config.options.saveToHistory">保存到历史记录</el-checkbox>
          </div>
        </div>
      </div>

      <div class="config-section">
        <h3>模型配置</h3>
        <div class="model-config-grid">
          <div class="config-item">
            <label>剧本生成模型</label>
            <el-select v-model="config.models.script" class="select" :loading="loadingModels">
              <el-option v-for="model in textModels" :key="model.identifier" :label="model.name" :value="model.identifier">
                <div class="model-option">
                  <img v-if="model.image_url" :src="model.image_url" alt="模型图片" class="model-icon">
                  <span class="model-name">{{ model.name }}</span>
                  <el-tag v-if="model.is_hot" size="small" type="danger" class="model-tag">热门</el-tag>
                  <el-tag v-if="model.is_new" size="small" type="primary" class="model-tag">新品</el-tag>
                </div>
              </el-option>
            </el-select>
          </div>
          <div class="config-item">
            <label>分镜生成模型</label>
            <el-select v-model="config.models.storyboard" class="select" :loading="loadingModels">
              <el-option v-for="model in textModels" :key="model.identifier" :label="model.name" :value="model.identifier">
                <div class="model-option">
                  <img v-if="model.image_url" :src="model.image_url" alt="模型图片" class="model-icon">
                  <span class="model-name">{{ model.name }}</span>
                  <el-tag v-if="model.is_hot" size="small" type="danger" class="model-tag">热门</el-tag>
                  <el-tag v-if="model.is_new" size="small" type="primary" class="model-tag">新品</el-tag>
                </div>
              </el-option>
            </el-select>
          </div>
          <div class="config-item">
            <label>角色生成模型</label>
            <el-select v-model="config.models.character" class="select" :loading="loadingModels">
              <el-option v-for="model in imageModels" :key="model.identifier" :label="model.name" :value="model.identifier">
                <div class="model-option">
                  <img v-if="model.image_url" :src="model.image_url" alt="模型图片" class="model-icon">
                  <span class="model-name">{{ model.name }}</span>
                  <el-tag v-if="model.is_hot" size="small" type="danger" class="model-tag">热门</el-tag>
                  <el-tag v-if="model.is_new" size="small" type="primary" class="model-tag">新品</el-tag>
                </div>
              </el-option>
            </el-select>
          </div>
          <div class="config-item">
            <label>场景生成模型</label>
            <el-select v-model="config.models.scene" class="select" :loading="loadingModels">
              <el-option v-for="model in imageModels" :key="model.identifier" :label="model.name" :value="model.identifier">
                <div class="model-option">
                  <img v-if="model.image_url" :src="model.image_url" alt="模型图片" class="model-icon">
                  <span class="model-name">{{ model.name }}</span>
                  <el-tag v-if="model.is_hot" size="small" type="danger" class="model-tag">热门</el-tag>
                  <el-tag v-if="model.is_new" size="small" type="primary" class="model-tag">新品</el-tag>
                </div>
              </el-option>
            </el-select>
          </div>
          <div class="config-item">
            <label>视频合成模型</label>
            <el-select v-model="config.models.video" class="select" :loading="loadingModels">
              <el-option v-for="model in videoModels" :key="model.identifier" :label="model.name" :value="model.identifier">
                <div class="model-option">
                  <img v-if="model.image_url" :src="model.image_url" alt="模型图片" class="model-icon">
                  <span class="model-name">{{ model.name }}</span>
                  <el-tag v-if="model.is_hot" size="small" type="danger" class="model-tag">热门</el-tag>
                  <el-tag v-if="model.is_new" size="small" type="primary" class="model-tag">新品</el-tag>
                </div>
              </el-option>
            </el-select>
          </div>
        </div>
      </div>

      <div class="actions">
        <el-button type="primary" @click="saveConfig">保存设置</el-button>
        <el-button @click="resetConfig">重置</el-button>
      </div>
    </div>

    <el-dialog title="添加风格" :visible.sync="addStyleDialogVisible" width="30%">
      <el-form :model="newStyle" label-width="100px">
        <el-form-item label="风格名称">
          <el-input v-model="newStyle.name" placeholder="如：现代都市"></el-input>
        </el-form-item>
        <el-form-item label="风格描述">
          <el-input v-model="newStyle.description" placeholder="如：适合现代都市题材"></el-input>
        </el-form-item>
        <el-form-item label="启用状态">
          <el-switch v-model="newStyle.enabled"></el-switch>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="addStyleDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="addStyle">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import appCache from '../../utils/appCache'

export default {
  name: 'DramaConfig',
  data() {
    return {
      config: {
        defaults: {
          duration: '3',
          style: 'modern'
        },
        maxScriptLength: 2000,
        autoStoryboard: true,
        styles: {
          modern: { name: '现代', description: '现代都市题材', enabled: true },
          ancient: { name: '古风', description: '古代题材', enabled: true },
          fantasy: { name: '奇幻', description: '奇幻题材', enabled: false },
          sciFi: { name: '科幻', description: '科幻题材', enabled: false }
        },
        templates: {
          storyboard: '根据剧本内容生成详细的分镜脚本，包含场景描述、镜头角度、角色动作等',
          character: '根据角色设定生成详细的角色形象描述，包括外貌、服装、性格等',
          scene: '根据剧本内容生成场景描述，包括环境、氛围、道具等'
        },
        options: {
          generateAudio: true,
          generateSubtitles: true,
          optimizeQuality: false,
          saveToHistory: true
        },
        models: {
          script: 'gpt-3.5-turbo',
          storyboard: 'gpt-3.5-turbo',
          character: 'stable-diffusion',
          scene: 'stable-diffusion',
          video: 'runway-gen2'
        }
      },
      addStyleDialogVisible: false,
      newStyle: {
        name: '',
        description: '',
        enabled: true
      },
      textModels: [],
      imageModels: [],
      videoModels: [],
      loadingModels: false
    }
  },
  methods: {
    async loadConfig() {
      try {
        const response = await axios.get('/admin/config/drama')
        if (response.data.code === 200) {
          this.config = response.data.data
        }
      } catch (error) {
        console.error('加载配置失败:', error)
      }
    },
    async loadModels() {
      this.loadingModels = true
      try {
        const [textModels, imageModels, videoModels] = await Promise.all([
          appCache.getModelsByCategory('text'),
          appCache.getModelsByCategory('image'),
          appCache.getModelsByCategory('video')
        ])
        this.textModels = textModels
        this.imageModels = imageModels
        this.videoModels = videoModels
      } catch (error) {
        console.error('加载模型失败:', error)
      } finally {
        this.loadingModels = false
      }
    },
    async saveConfig() {
      try {
        const response = await axios.post('/admin/config/drama', this.config)
        if (response.data.code === 200) {
          this.$message.success('保存成功')
        }
      } catch (error) {
        console.error('保存配置失败:', error)
        this.$message.error('保存失败')
      }
    },
    resetConfig() {
      this.$confirm('确定要重置所有配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.config = {
          defaults: {
            duration: '3',
            style: 'modern'
          },
          maxScriptLength: 2000,
          autoStoryboard: true,
          styles: {},
          templates: {
            storyboard: '',
            character: '',
            scene: ''
          },
          options: {
            generateAudio: true,
            generateSubtitles: true,
            optimizeQuality: false,
            saveToHistory: true
          },
          models: {
            script: 'gpt-3.5-turbo',
            storyboard: 'gpt-3.5-turbo',
            character: 'stable-diffusion',
            scene: 'stable-diffusion',
            video: 'runway-gen2'
          }
        }
        this.$message.info('已重置')
      }).catch(() => {})
    },
    addStyle() {
      const key = this.newStyle.name.toLowerCase().replace(/\s+/g, '_')
      this.$set(this.config.styles, key, {
        name: this.newStyle.name,
        description: this.newStyle.description,
        enabled: this.newStyle.enabled
      })
      this.newStyle = { name: '', description: '', enabled: true }
      this.addStyleDialogVisible = false
      this.$message.success('添加成功')
    },
    removeStyle(key) {
      this.$delete(this.config.styles, key)
    }
  },
  mounted() {
    this.loadConfig()
    this.loadModels()
  }
}
</script>

<style scoped>
.drama-config-container {
  padding: 20px 0;
}

.drama-config-content {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: #333;
}

.drama-config-header {
  margin-bottom: 30px;
}

.drama-config-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.drama-config-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.config-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.config-section:last-of-type {
  border-bottom: none;
}

.config-section h3 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.config-item {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.config-item label {
  font-weight: 600;
  color: #333;
}

.select {
  width: 100%;
}

.number-input {
  width: 120px;
}

.length-input-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.unit {
  color: #666;
  white-space: nowrap;
}

.style-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.template-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.template-item {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.template-item label {
  font-weight: 600;
  color: #333;
}

.template-input {
  width: 100%;
  resize: vertical;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.option-item {
  display: flex;
  align-items: center;
}

.model-config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.model-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.model-icon {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
}

.model-name {
  flex: 1;
}

.model-tag {
  margin-left: 4px;
}

.actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}
</style>
